import { ReactElement, ReactNode, useId } from 'react'
import { HorizontalScroll } from '../HorizontalScroll'
import { salesReportIcons } from '../icons'
import * as S from '../SummaryTable/styles'

export interface GroupTableColumn<V> {
  id: string
  label: string
  width: string
  minWidth: number
  align: 'left' | 'right'
  render: (values: V) => ReactNode
}

export interface GroupTableGroup<V> {
  key: string
  label: string
  badge?: ReactNode
  values: V
  expanded?: boolean
  onToggle?: () => void
  detail?: ReactNode
}

export interface GroupTableSort {
  key: string
  direction: 'asc' | 'desc'
}

interface GroupTableProps<V> {
  groupLabel: string
  ariaLabel?: string
  columns: Array<GroupTableColumn<V>>
  groups: Array<GroupTableGroup<V>>
  total: { label: string, values: V }
  sort: GroupTableSort | null
  onSort: (key: string) => void
  allExpanded?: boolean
  onToggleAll?: () => void
  detailMinWidth?: number
}

const LABEL_WIDTH = 'minmax(150px, 1.8fr)'
const LABEL_MIN_WIDTH = 150
const DETAIL_HORIZONTAL_PADDING = 20

const ariaSort = (sort: GroupTableSort | null, key: string): 'ascending' | 'descending' | 'none' => {
  if (sort === null || sort.key !== key) return 'none'
  return sort.direction === 'asc' ? 'ascending' : 'descending'
}

function SortHeader({
  id,
  label,
  align,
  sort,
  onSort,
}: {
  id: string
  label: string
  align: 'left' | 'right'
  sort: GroupTableSort | null
  onSort: (key: string) => void
}): ReactElement {
  const state = ariaSort(sort, id)
  return (
    <S.Cell role="columnheader" aria-sort={state} $align={align} $sticky={id === 'label'}>
      <S.SortButton type="button" onClick={() => onSort(id)}>
        {label}
        <S.SortMark aria-hidden="true">{state === 'ascending' ? '▲' : state === 'descending' ? '▼' : '↕'}</S.SortMark>
      </S.SortButton>
    </S.Cell>
  )
}

function GroupRow<V>({
  group,
  columns,
  template,
}: {
  group: GroupTableGroup<V>
  columns: Array<GroupTableColumn<V>>
  template: string
}): ReactElement {
  const colSpan = columns.length + 1
  const detailId = useId()
  const expandable = group.detail !== undefined && group.onToggle !== undefined
  const expanded = expandable && group.expanded === true
  const label = (
    <>
      <S.Label title={group.label}>{group.label}</S.Label>
      {group.badge}
    </>
  )
  return (
    <>
      <S.Row role="row" $template={template} $open={expanded} $static={!expandable} onClick={expandable ? group.onToggle : undefined}>
        <S.Cell role="cell" $sticky>
          {expandable ? (
            <S.RowToggle
              type="button"
              aria-expanded={expanded}
              aria-controls={detailId}
              onClick={(event) => {
                event.stopPropagation()
                group.onToggle?.()
              }}
            >
              <S.Chevron src={salesReportIcons.chevronDown} alt="" width={16} height={16} $open={expanded} />
              {label}
            </S.RowToggle>
          ) : (
            <S.StaticLabel>{label}</S.StaticLabel>
          )}
        </S.Cell>
        {columns.map((column) => (
          <S.Cell key={column.id} role="cell" $align={column.align}>
            {column.render(group.values)}
          </S.Cell>
        ))}
      </S.Row>
      {expandable && (
        <S.DetailRow role="row" hidden={!expanded}>
          <S.Detail id={detailId} role="cell" aria-colspan={colSpan} hidden={!expanded}>
            {expanded ? group.detail : null}
          </S.Detail>
        </S.DetailRow>
      )}
    </>
  )
}

export function GroupTable<V>({
  groupLabel,
  ariaLabel,
  columns,
  groups,
  total,
  sort,
  onSort,
  allExpanded = false,
  onToggleAll,
  detailMinWidth = 0,
}: GroupTableProps<V>): ReactElement {
  const template = [LABEL_WIDTH, ...columns.map((column) => column.width)].join(' ')
  const minWidth = Math.max(
    LABEL_MIN_WIDTH + columns.reduce((sum, column) => sum + column.minWidth, 0),
    detailMinWidth === 0 ? 0 : detailMinWidth + DETAIL_HORIZONTAL_PADDING
  )
  const label = ariaLabel ?? `Resumo por ${groupLabel.toLowerCase()}`

  return (
    <S.Wrapper>
      {onToggleAll !== undefined && groups.length > 1 && (
        <S.ToggleAll type="button" onClick={onToggleAll}>
          {allExpanded ? 'Recolher todos' : 'Expandir todos'}
        </S.ToggleAll>
      )}
      <S.Frame>
        <HorizontalScroll label={`${label}, com rolagem lateral`}>
          <S.Table role="table" aria-label={label} aria-colcount={columns.length + 1} $minWidth={minWidth}>
            <S.HeaderRow role="row" $template={template}>
              <SortHeader id="label" label={groupLabel} align="left" sort={sort} onSort={onSort} />
              {columns.map((column) => (
                <SortHeader key={column.id} id={column.id} label={column.label} align={column.align} sort={sort} onSort={onSort} />
              ))}
            </S.HeaderRow>
            {groups.map((group) => (
              <GroupRow key={group.key} group={group} columns={columns} template={template} />
            ))}
            <S.TotalRow role="row" $template={template}>
              <S.Cell role="rowheader" $strong $sticky>
                {total.label}
              </S.Cell>
              {columns.map((column) => (
                <S.Cell key={column.id} role="cell" $align={column.align} $strong>
                  {column.render(total.values)}
                </S.Cell>
              ))}
            </S.TotalRow>
          </S.Table>
        </HorizontalScroll>
      </S.Frame>
    </S.Wrapper>
  )
}
